import React, { useMemo } from 'react';
import { useIntl, getLocale } from 'umi';
import ChartsIndex from '@intelligence/components/ChartsComponents/ChartsIndex';
import useBarCharts from '@intelligence/components/Chart/hooks/useBarCharts';
import useLineCharts from '@intelligence/components/Chart/hooks/useLineCharts';
import { showTimeByDateType } from '@intelligence/utils/commonUtils';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import { SENSORTOWER_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';
import { ECHARTSCOLOR } from '@intelligence/utils/echartsConst';
import { mobileDetailsOverlapSegOptions } from '@intelligence/pages/TopCharts/_config/metricOptions';
import {
  formatYAxisLabel,
  formatMetricValue,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/utils';

import type { IBaseProps, IDateType } from '@intelligence/types/common';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';
import type {
  IMobileDetailsOverlapRank,
  IMobileDetailsOverlapTrend,
  IMobileDetailsOverlapRanksFetchParams,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/types';

interface IMobileDetailsOverlapVisualisContentProps extends IBaseProps {
  imageType: any;
  topChartsType: string;
  cardRef: React.RefObject<HTMLDivElement>;
  chartsInstance: React.RefObject<IChartsIndexInstance>;
  metricSegVal: string;
  chartType: ChartType;
  tableData: IMobileDetailsOverlapRank[];
  lineData: IMobileDetailsOverlapTrend[];
  dateType: IDateType;
  fetchParams: IMobileDetailsOverlapRanksFetchParams;
  chartLoading: boolean;
  setChartLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isAggregate: boolean;
}

const MobileDetailsOverlapVisualisContent: React.FC<IMobileDetailsOverlapVisualisContentProps> = (props) => {
  const {
    imageType,
    topChartsType,
    cardRef,
    chartsInstance,
    metricSegVal,
    chartType,
    tableData,
    lineData,
    dateType,
    fetchParams,
    chartLoading,
    setChartLoading,
    isAggregate,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const metricSegValTitle = useMemo<string>(() => {
    const metricSeg = mobileDetailsOverlapSegOptions?.find((option: any) => option?.value === metricSegVal);
    const metricSegText = metricSeg?.text;
    if (!metricSegText) return '';
    return formatMessage({ id: metricSegText });
  }, [locale, metricSegVal]);

  const barNameList = useMemo<string[]>(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData.map((item: any) => item?.name);
  }, [tableData]);

  const barSeriesData = useMemo<any[]>(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData.map((item: any, i: number): any => {
      return {
        type: 'bar',
        name: item?.name,
        value: item.metric_list?.find((item: any) => item?.metric === metricSegVal)?.value || null,
        cover: item?.cover,
        itemStyle: {
          color: ECHARTSCOLOR[i],
        },
        label: {
          show: true,
          position: 'top',
          color: '#444',
          distance: 4,
          fontSize: 12,
          formatter: (params: any) => {
            return formatMetricValue(metricSegVal, params?.value);
          },
        },
      };
    });
  }, [tableData, metricSegVal]);

  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    seriesData: barSeriesData,
    barOrPieData: tableData,
    setChartLoading,
    showTitle: false,
    title: metricSegVal,
    titleNow: metricSegValTitle,
    barNameList,
    imageType,
    topChartsType,
    cardWidth: (cardRef.current as any)?.clientWidth,
    chartType: ChartType.Bar,
    startDate: fetchParams?.start_date,
    endDate: fetchParams?.end_date,
    yAxisFormatter: (value: any) => {
      return formatYAxisLabel(metricSegVal, value);
    },
    valueFormatter: (params: any) => {
      return formatMetricValue(metricSegVal, params?.value);
    },
  });

  // 组装rpd折线图数据
  const getRpdTypeData = (list: any, metric = '') => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any[] = [];
    for (const item of list) {
      arr.push({
        time: showTimeByDateType(item?.time, dateType?.toLocaleLowerCase()),
        value: item?.metric_value_list?.find((item: any) => item?.metric === metric)?.value || null,
      });
    }
    return arr;
  };

  const getMetricsList = (list: any, metric: string) => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any = [];
    for (const item of list) {
      arr.push({
        id: item?.id,
        name: item?.name,
        cover: item?.cover,
        history: getRpdTypeData(item?.trend_metric_list, metric),
      });
    }
    return arr;
  };

  const lineSeriesData = useMemo<{
    [key: string]: any[];
  }>(() => {
    const arrGameData: any = {};
    for (const mobileDetailsOverlapSegOption of mobileDetailsOverlapSegOptions) {
      const mobileDetailsOverlapSegOptionValue = mobileDetailsOverlapSegOption?.value;
      arrGameData[mobileDetailsOverlapSegOptionValue] = getMetricsList(lineData, mobileDetailsOverlapSegOptionValue);
    }
    return arrGameData;
  }, [lineData]);

  // // 获取折线图hooks
  const { lineOption } = useLineCharts({
    gridConfig: { top: 16 },
    isPercentLine: metricSegVal === SENSORTOWER_METRIC_KEY_MAP.CROSS_APP_USAGE,
    lineData: lineSeriesData,
    metric: metricSegVal,
    topChartsType,
    imageType,
    dateType,
    titleNow: metricSegValTitle,
    chartType: ChartType.Line,
    yAxisFormatter: (value: any) => {
      return formatYAxisLabel(metricSegVal, value);
    },
    valueFormatter: (params: any) => {
      return formatMetricValue(metricSegVal, params?.value?.[1]);
    },
  });

  const barOptionTmp = useMemo(() => {
    if (isAggregate) return barOption;
    const seriesTmp = lineOption?.series?.map((serie: any) => {
      const dataTmp = serie?.data?.map((serieData: any) => {
        return {
          ...serieData,
          itemStyle: {
            ...serieData?.itemStyle,
            opacity: 1,
          },
        };
      });
      return {
        ...serie,
        type: 'bar',
        data: dataTmp || [],
        label: {
          show: false,
          position: 'top',
          color: '#444',
          distance: 4,
          fontSize: 12,
          formatter: (params: any) => {
            return formatMetricValue(metricSegVal, params?.value?.[1]);
          },
        },
      };
    });
    return {
      ...lineOption,
      series: seriesTmp || [],
    };
  }, [isAggregate, barOption, lineOption]);

  return (
    <ChartsIndex
      ref={chartsInstance}
      chartLoading={chartLoading}
      isNodata={!(lineData?.length || tableData?.length)}
      chartType={chartType}
      barOption={barOptionTmp}
      lineOption={lineOption}
    />
  );
};

export default React.memo(MobileDetailsOverlapVisualisContent);
